import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Monitor, Smartphone, Tablet, AlertCircle, CheckCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const SessionsManagementPage = () => {
  const { user } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [revoking, setRevoking] = useState(null);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    loadSessions();
  }, []);

  const loadSessions = async () => {
    setLoading(true);
    try {
      // In production, this would call your backend API
      // For now, we'll use mock data from localStorage
      const savedSessions = JSON.parse(localStorage.getItem(`sessions_${user?.user_id}`) || '[]');
      
      // If no sessions exist, create a current session
      if (savedSessions.length === 0) {
        const currentSession = {
          id: 'current',
          device: getDeviceInfo(),
          location: 'Current location',
          ip: 'Current IP',
          lastActive: new Date().toISOString(),
          isCurrent: true
        };
        savedSessions.push(currentSession);
        localStorage.setItem(`sessions_${user?.user_id}`, JSON.stringify(savedSessions));
      }
      
      setSessions(savedSessions);
    } catch (err) {
      setError('Failed to load sessions');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const getDeviceInfo = () => {
    const ua = navigator.userAgent;
    if (/Mobile/.test(ua)) return { type: 'mobile', name: 'Mobile Device', icon: Smartphone };
    if (/Tablet/.test(ua)) return { type: 'tablet', name: 'Tablet', icon: Tablet };
    return { type: 'desktop', name: 'Desktop', icon: Monitor };
  };

  const handleRevokeSession = async (sessionId) => {
    if (!window.confirm('Are you sure you want to sign out of this session?')) {
      return;
    }

    setRevoking(sessionId);
    setError('');
    
    try {
      // In production, call your backend API to revoke the session
      const updatedSessions = sessions.filter(s => s.id !== sessionId);
      localStorage.setItem(`sessions_${user?.user_id}`, JSON.stringify(updatedSessions));
      setSessions(updatedSessions);
      setSuccess('Session revoked successfully');
      
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to revoke session');
    } finally {
      setRevoking(null);
    }
  };

  const handleRevokeAllOthers = async () => {
    if (!window.confirm('Are you sure you want to sign out of all other sessions? You will need to sign in again on those devices.')) {
      return;
    }

    setLoading(true);
    try {
      // Keep only the current session
      const currentSession = sessions.find(s => s.isCurrent);
      const updatedSessions = currentSession ? [currentSession] : [];
      localStorage.setItem(`sessions_${user?.user_id}`, JSON.stringify(updatedSessions));
      setSessions(updatedSessions);
      setSuccess('All other sessions have been revoked');
      
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to revoke sessions');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <Link
            to="/settings/security"
            className="inline-flex items-center text-primary-600 hover:text-primary-700 font-medium mb-4"
          >
            ← Back to Security Settings
          </Link>
          <div className="flex items-center gap-3">
            <div className="bg-primary-600 p-3 rounded-full">
              <MapPin className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Where you're signed in</h1>
              <p className="text-gray-600 mt-1">
                Manage your active sessions across all devices
              </p>
            </div>
          </div>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        {success && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-green-800">{success}</p>
          </div>
        )}

        {/* Actions */}
        {sessions.filter(s => !s.isCurrent).length > 0 && (
          <div className="mb-6">
            <button
              onClick={handleRevokeAllOthers}
              disabled={loading}
              className="btn btn-secondary text-red-600 hover:bg-red-50 disabled:opacity-50"
            >
              Sign out of all other sessions
            </button>
          </div>
        )}

        {/* Sessions list */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">
              Active sessions ({sessions.length})
            </h2>
          </div>

          {loading ? (
            <div className="p-8 text-center text-gray-600">
              Loading sessions...
            </div>
          ) : sessions.length === 0 ? (
            <div className="p-8 text-center text-gray-600">
              No active sessions
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {sessions.map((session) => {
                const DeviceIcon = session.device?.icon || Monitor;
                return (
                  <div key={session.id} className="p-6">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-start gap-4 flex-1">
                        <div className="flex-shrink-0 mt-1">
                          <DeviceIcon className="w-6 h-6 text-gray-400" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="text-base font-medium text-gray-900">
                              {session.device?.name || 'Unknown Device'}
                            </h3>
                            {session.isCurrent && (
                              <span className="px-2 py-0.5 text-xs font-medium bg-green-100 text-green-700 rounded">
                                Current
                              </span>
                            )}
                          </div>
                          <div className="space-y-1 text-sm text-gray-600">
                            <p>{session.location || 'Unknown location'}</p>
                            <p className="text-xs">Last active: {formatDate(session.lastActive)}</p>
                            {session.ip && (
                              <p className="text-xs font-mono">{session.ip}</p>
                            )}
                          </div>
                        </div>
                      </div>
                      {!session.isCurrent && (
                        <button
                          onClick={() => handleRevokeSession(session.id)}
                          disabled={revoking === session.id}
                          className="btn btn-secondary text-sm whitespace-nowrap disabled:opacity-50"
                        >
                          {revoking === session.id ? 'Signing out...' : 'Sign out'}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Info box */}
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-blue-800">
            <strong>Note:</strong> If you see a session you don't recognize, sign out of it immediately
            and change your password to secure your account.
          </p>
        </div>
      </div>
    </div>
  );
};

export default SessionsManagementPage;
